import { formatMoney, formatPlate } from "@szerviz/core";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGE_CODES } from "@szerviz/i18n";
import { ErrorText } from "../components/ErrorText";
import { LanguageSelect } from "../components/LanguageSelect";
import { Logo } from "../components/Logo";
import { DocHead, MadeWith, Signatures, Stamp } from "../components/PrintDocs";
import { renderSVG } from "uqr";
import type { DataStore, PublicQuote } from "../data";
import { savedLanguage } from "../language";

/**
 * The customer's quote page, opened from the link the workshop sent
 * (#quote/<token>). No sign in: the secret token is the key. Phone first:
 * one card per line with a big tick box, the total follows the ticks.
 * The language is the workshop's, like the e-mail that brought the link (a
 * phone set to English still shows a Hungarian workshop's quote in Hungarian);
 * a language the customer chose here on this device wins (#112).
 */
export function QuotePage({ store, token }: { store: DataStore; token: string }) {
  const { t, i18n } = useTranslation();
  const [quote, setQuote] = useState<PublicQuote | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [yes, setYes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // the workshop's own logo (a short lived signed link; the page works the same without it)
  const [logo, setLogo] = useState<string | null>(null);
  // the A4 sheet is put in the page only for the moment of printing (button or the browser's own print)
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const before = () => flushSync(() => setPrinting(true));
    const after = () => setPrinting(false);
    globalThis.addEventListener?.("beforeprint", before);
    globalThis.addEventListener?.("afterprint", after);
    return () => {
      globalThis.removeEventListener?.("beforeprint", before);
      globalThis.removeEventListener?.("afterprint", after);
    };
  }, []);

  const load = async () => {
    try {
      const q = await store.quoteView(token);
      const lang = savedLanguage() ?? q.language;
      if (LANGUAGE_CODES.includes(lang) && lang !== i18n.language) {
        await i18n.changeLanguage(lang);
        if (typeof document !== "undefined") document.documentElement.lang = lang;
      }
      setQuote(q);
      setYes(q.answeredAt ? q.approvedIds : q.items.map((i) => i.id));
    } catch (err) {
      setError(err);
    }
  };
  useEffect(() => {
    void load();
    let live = true;
    store.quoteLogoUrl(token).then((u) => live && setLogo(u), () => undefined);
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const answer = async (ids: string[]) => {
    setBusy(true);
    setError(null);
    try {
      await store.quoteAnswer(token, ids);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!quote) {
    return <main className="quote-page">{error ? <ErrorText error={error} /> : <p>{t("common.loading")}</p>}</main>;
  }
  const money = (minor: number) => formatMoney(minor, quote.currency, i18n.language);
  const chosen = quote.items.filter((i) => yes.includes(i.id));
  const sum = chosen.reduce((a, i) => a + i.gross, 0);
  const date = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: iso.length > 10 ? "short" : undefined }).format(new Date(iso));
  const done = !!quote.answeredAt;
  const here = globalThis.location?.href ?? "";

  return (
    <main className="quote-page">
      <header className="qp-head">
        <div className="qp-brand">
          {logo && <img className="qp-logo" src={logo} alt={t("logo.alt", { name: quote.workshopName })} />}
          <strong className="qp-shop">
            {quote.workshopName}
            <span className="qp-by">
              <Logo size={16} word={false} onDark /> {t("quote.page.by")}
            </span>
          </strong>
        </div>
        {quote.workshopPhone && (
          <a className="button" href={`tel:${quote.workshopPhone.replace(/[^+\d]/g, "")}`}>
            {t("quote.page.call", { phone: quote.workshopPhone })}
          </a>
        )}
      </header>
      <h1>{t("quote.page.title")}</h1>
      <p className="qp-car">
        {quote.plateNumber && <strong>{formatPlate(quote.plateNumber, quote.plateCountry ?? "HU")}</strong>} {quote.vehicle} · {quote.number}
      </p>
      {done ? (
        <p className="ok-band" role="status">
          {t("quote.page.thanks", { date: date(quote.answeredAt!) })}
        </p>
      ) : (
        <div className="qp-letter">
          <p>
            <strong>{quote.customerName ? t("quote.page.greeting", { name: quote.customerName }) : t("quote.page.greetingNoName")}</strong>
          </p>
          <p>{t("quote.page.from", { workshop: quote.workshopName })}</p>
          <p>{t("quote.page.why")}</p>
          <p>{t("quote.page.intro")}</p>
        </div>
      )}

      {quote.findings && (
        <section className="qp-findings" aria-label={t("quote.page.findings")}>
          <h2>{t("quote.page.findings")}</h2>
          <p>{quote.findings}</p>
        </section>
      )}

      <ul className="qp-items">
        {quote.items.map((i) => {
          const on = yes.includes(i.id);
          return (
            <li key={i.id} className={on ? "on" : "off"}>
              <label>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={done}
                  aria-label={i.description}
                  onChange={(e) => setYes((y) => (e.target.checked ? [...y, i.id] : y.filter((x) => x !== i.id)))}
                />
                <span className="qp-desc">
                  {i.description}
                  {i.optional && <small className="tag-optional"> {t("quote.optional")}</small>}
                  <small className="muted">
                    {i.quantity.toLocaleString(i18n.language)} {i.unit}
                  </small>
                </span>
                <span className="qp-price">{money(i.gross)}</span>
              </label>
              {done && <span className={`approval approval-${on ? "approved" : "declined"}`}>{t(`quote.approval.${on ? "approved" : "declined"}`)}</span>}
            </li>
          );
        })}
      </ul>

      <p className="qp-total">
        {t(done ? "quote.page.approvedTotal" : "quote.page.selectedTotal")}: <strong>{money(sum)}</strong>
        <small className="muted"> {t("quote.page.gross")}</small>
      </p>

      <ErrorText error={error} />
      {!done && (
        <div className="qp-actions">
          <button type="button" className="primary" disabled={busy || chosen.length === 0} onClick={() => void answer(yes)}>
            {t("quote.page.approve", { count: chosen.length })}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (globalThis.confirm?.(t("quote.page.declineConfirm"))) void answer([]);
            }}
          >
            {t("quote.page.declineAll")}
          </button>
          <p className="muted small">{t("quote.page.until", { date: date(quote.expiresAt) })}</p>
        </div>
      )}
      <button type="button" className="qp-print" onClick={() => {
          flushSync(() => setPrinting(true));
          globalThis.print?.();
        }}>
        {t("workOrder.printQuote")}
      </button>
      <footer className="qp-foot">
        {quote.workshopPhone && <p>{t("quote.page.question")}</p>}
        <p className="muted small">{t("quote.page.safe")}</p>
        <LanguageSelect />
        <p className="muted small">
          {quote.workshopName}
          {quote.workshopAddress && <> · {quote.workshopAddress}</>}
          {quote.workshopPhone && <> · {quote.workshopPhone}</>}
        </p>
      </footer>
      {/* the same quote as an A4 sheet when printed (only shown on paper) */}
      {printing && (
      <section className="print-sheet qp-sheet ps-doc-quote">
        <DocHead
          logo={logo ? <img className="ps-logo" src={logo} alt={t("logo.alt", { name: quote.workshopName })} /> : null}
          shop={quote.workshopName}
          lines={[[quote.workshopAddress, quote.workshopPhone].filter(Boolean).join(" · ")]}
          title={t("workOrder.print.quoteTitle")}
          number={quote.number}
          meta={`${t("workOrder.print.validUntil")}: ${date(quote.expiresAt)}`}
        />
        <div className="ps-boxes">
          <section>
            <p className="ps-cap">{t("workOrder.vehicle")}</p>
            <p>
              <strong>{quote.plateNumber && formatPlate(quote.plateNumber, quote.plateCountry ?? "HU")}</strong> {quote.vehicle}
            </p>
          </section>
          <section>
            <p className="ps-cap">{quote.customerName ? t("quote.page.greeting", { name: quote.customerName }) : t("quote.page.greetingNoName")}</p>
            <p className="ps-letter">{t("workOrder.print.quoteIntro")}</p>
          </section>
        </div>
        {quote.findings && (
          <section className="ps-text">
            <p className="ps-cap">{t("quote.page.findings")}</p>
            <p>{quote.findings}</p>
          </section>
        )}
        <table className="ps-items">
          <thead>
            <tr>
              <th>{t("workOrder.item.description")}</th>
              <th className="num">{t("workOrder.item.quantity")}</th>
              <th className="num">{t("workOrder.gross")}</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((i) => {
              const state = !done ? "pending" : yes.includes(i.id) ? "approved" : "declined";
              return (
                <tr key={i.id} className={state === "declined" ? "ps-declined" : undefined}>
                  <td>
                    <span className="ps-desc">{i.description}</span>
                    <span className={`ps-mark ps-mark-${state}`}>{t(`quote.approval.${state}`)}</span>
                  </td>
                  <td className="num">
                    {i.quantity.toLocaleString(i18n.language)} {i.unit}
                  </td>
                  <td className="num">{money(i.gross)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="ps-sum">
          <div className="ps-sum-left">{done && <Stamp label={t("workOrder.print.stampAnswered")} when={date(quote.answeredAt!)} />}</div>
          <div className="ps-totals">
            <div className="ps-grand">
              <span>{t("workOrder.print.expectedTotal")}</span>
              <span>{money(sum)}</span>
            </div>
          </div>
        </div>
        {!done && here && (
          <section className="ps-qr">
            <div className="ps-qr-code" dangerouslySetInnerHTML={{ __html: renderSVG(here, { border: 1 }) }} />
            <div>
              <div className="ps-qr-title">{t("workOrder.print.quoteQrTitle")}</div>
              <p>{t("workOrder.print.quoteQrText")}</p>
              <p className="mono ps-link">{here}</p>
            </div>
          </section>
        )}
        <p className="ps-note">{t("workOrder.print.quoteNote")}</p>
        <div className="ps-bottom">
          <Signatures left={t("workOrder.print.signQuoteWorkshop")} right={t("workOrder.print.signQuoteCustomer")} />
          <div className="ps-foot">
            <MadeWith />
          </div>
        </div>
      </section>
      )}
    </main>
  );
}
